import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortingState } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

const WEBHOOKS_QUERY_KEY = 'webhooks';

export const getWebhooksQueryKey = () => [WEBHOOKS_QUERY_KEY] as const;

const getWebhooksParamQueryKey = ({
  sortingState,
  filter,
}: {
  sortingState?: SortingState;
  filter?: { subcategory?: string[] };
}) => [WEBHOOKS_QUERY_KEY, sortingState, filter] as const;

const getWebhooksQueryOptions = ({
  sortingState,
  filter,
  enabled,
}: {
  sortingState?: SortingState;
  filter?: { subcategory?: string[] };
  enabled: boolean;
}) =>
  infiniteQueryOptions({
    queryKey: getWebhooksParamQueryKey({ sortingState, filter }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.notifications.listWebhooks({
        filterSubcategories: filter?.subcategory,
        pageLimit: '25',
        pageCursor: pageParam,
        sortField: sortingState?.columnId,
        sortOrder: sortingState?.sortBy.direction || 'desc',
      });
      return data || null;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
    enabled,
  });

type UseGetWebhooksQueryArgs = {
  sortingState?: SortingState;
  enabled?: boolean;
  filter?: {
    subcategory?: string[];
  };
};

export const useGetWebhooksQuery = ({
  filter,
  sortingState,
  enabled = true,
}: UseGetWebhooksQueryArgs = {}) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(
    getWebhooksQueryOptions({ sortingState, filter, enabled })
  );
  return {
    webhooks: data?.pages
      ? flatMap(data.pages, (page) => page.items)
      : undefined,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  };
};
